import {IconButton} from '@dropbox/dig-components/buttons';
import {Tooltip} from '@dropbox/dig-components/tooltips';
import {UIIcon} from '@dropbox/dig-icons';
import {AlignHorizontalLine, AlignLeftLine, AlignRightLine} from '@dropbox/dig-icons/assets';
import {useLexicalComposerContext} from '@lexical/react/LexicalComposerContext';
import {t} from 'i18next';
import {$getNodeByKey, NodeKey} from 'lexical';

import {$isImageNode, ImageLayout} from './ImageNode';
import styles from './ImageNode.module.css';

const OPTIONS: ReadonlyArray<{layout: ImageLayout; icon: typeof AlignLeftLine; label: string}> = [
  {layout: 'left', icon: AlignLeftLine, label: 'image_layout_wrap_left'},
  {layout: 'inline', icon: AlignHorizontalLine, label: 'image_layout_full_width'},
  {layout: 'right', icon: AlignRightLine, label: 'image_layout_wrap_right'},
];

/** Floating full-width / wrap-left / wrap-right picker shown over a selected image. */
export const ImageLayoutControls = ({layout, nodeKey}: {layout: ImageLayout; nodeKey: NodeKey}) => {
  const [editor] = useLexicalComposerContext();

  const setLayout = (next: ImageLayout) =>
    editor.update(() => {
      const node = $getNodeByKey(nodeKey);
      if ($isImageNode(node)) {
        node.setLayout(next);
      }
    });

  return (
    <span className={styles.layoutControls} role="toolbar" aria-label="Image layout">
      {OPTIONS.map((option) => (
        <Tooltip key={option.layout} title={t(option.label)}>
          <IconButton
            type="button"
            size="small"
            variant={option.layout === layout ? 'outline' : 'borderless'}
            aria-label={t(option.label)}
            aria-pressed={option.layout === layout}
            // Keep the image's node selection while clicking.
            onMouseDown={(event: React.MouseEvent) => event.preventDefault()}
            onClick={(event: React.MouseEvent) => {
              event.stopPropagation();
              setLayout(option.layout);
            }}
          >
            <UIIcon src={option.icon} />
          </IconButton>
        </Tooltip>
      ))}
    </span>
  );
};
